import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GalleryPageData, GalleryItem } from '@cng/shared';
import { SmartImage, SmartVideo } from '@/components/common/SmartImage';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/common/Icons';
import { PageBranches } from '@/components/locations/PageBranches';

type GalleryItemWithMedia = GalleryItem & {
  media?: {
    url: string; alt: string; width: number | null; height: number | null;
    derivatives?: { name: string; width: number; url: string; format: string }[];
    mediaType?: string; poster?: string | null;
  } | null;
};

export function GalleryPage({ data }: { data: GalleryPageData & { kind: 'gallery' } }) {
  const items = data.items as GalleryItemWithMedia[];
  const [category, setCategory] = useState('all');
  const [lightbox, setLightbox] = useState<number | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  const visible = useMemo(
    () => (category === 'all' ? items : items.filter((i) => i.category === category)),
    [items, category],
  );

  const close = useCallback(() => {
    setLightbox(null);
    lastFocused.current?.focus();
  }, []);

  const step = useCallback((delta: number) => {
    setLightbox((current) => {
      if (current === null) return null;
      return (current + delta + visible.length) % visible.length;
    });
  }, [visible.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the overlay from scrolling.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightbox, close, step]);

  const open = (index: number, event: React.MouseEvent | React.KeyboardEvent) => {
    lastFocused.current = event.currentTarget as HTMLElement;
    setLightbox(index);
  };

  const active = lightbox === null ? null : visible[lightbox];
  const heroSection = data.page.sections.find((s) => s.type === 'hero')?.data ?? {};
  // A page naming its own photos is a landing page that embeds a slice of the
  // gallery, not the gallery page — the original heads it with a plain line.
  const embedded = (data.page.galleryItemIds ?? []).length > 0;

  return (
    <>
    <div className="cng-gallery-page">
      <header className={`cng-gallery-hero${embedded ? ' is-compact' : ''}`}>
        <h1>{String(heroSection.title ?? data.page.title)}</h1>
        {heroSection.subtitle ? <p>{String(heroSection.subtitle)}</p> : null}
      </header>

      {data.categories.length > 1 && (
        <div className="cng-gallery-filters" role="group" aria-label="Filter photos by category">
          <button
            type="button"
            className={category === 'all' ? 'is-active' : undefined}
            aria-pressed={category === 'all'}
            onClick={() => setCategory('all')}
          >
            All ({items.length})
          </button>
          {data.categories.map((c) => {
            const count = items.filter((i) => i.category === c).length;
            return (
              <button
                type="button"
                key={c}
                className={category === c ? 'is-active' : undefined}
                aria-pressed={category === c}
                onClick={() => setCategory(c)}
              >
                {c} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div className="elementor-image-gallery">
        <div className="gallery gallery-columns-5 gallery-size-large">
          {visible.map((item, index) => (
            <figure className="gallery-item" key={item._id}>
              <button
                type="button"
                className="gallery-icon portrait"
                onClick={(e) => open(index, e)}
                aria-label={`Open "${item.alt || item.title || 'photo'}" in a larger view`}
              >
                {item.mediaType === 'video' ? (
                  <SmartVideo media={item.media} autoPlay={false} muted controls={false} poster={item.media?.poster ?? null} />
                ) : (
                  <SmartImage
                    media={item.media}
                    alt={item.alt || item.title}
                    sizes="(max-width: 520px) 100vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 252px"
                  />
                )}
              </button>
              {item.caption ? <figcaption className="gallery-caption">{item.caption}</figcaption> : null}
            </figure>
          ))}
        </div>
      </div>

      {visible.length === 0 && (
        <p className="cng-gallery-empty">No photos in this category yet.</p>
      )}

      {active ? (
        <div
          className="cng-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={active.alt || active.title || 'Photo'}
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <button type="button" className="cng-lightbox-close" onClick={close} ref={closeRef} aria-label="Close">
            <CloseIcon />
          </button>
          {visible.length > 1 && (
            <button type="button" className="cng-lightbox-prev" onClick={() => step(-1)} aria-label="Previous photo">
              <ChevronLeftIcon size={28} />
            </button>
          )}
          <figure className="cng-lightbox-figure">
            {active.mediaType === 'video' ? (
              <SmartVideo media={active.media} autoPlay controls muted={false} poster={active.media?.poster ?? null} />
            ) : (
              <SmartImage media={active.media} alt={active.alt || active.title} loading="eager" sizes="90vw" />
            )}
            {(active.caption || active.title) && (
              <figcaption>{active.caption || active.title}</figcaption>
            )}
          </figure>
          {visible.length > 1 && (
            <button type="button" className="cng-lightbox-next" onClick={() => step(1)} aria-label="Next photo">
              <ChevronRightIcon size={28} />
            </button>
          )}
          <p className="cng-lightbox-count" aria-live="polite">
            {lightbox! + 1} of {visible.length}
          </p>
        </div>
      ) : null}
    </div>
    <PageBranches sections={data.page.sections} />
    </>
  );
}
